import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'motion/react';
import LiquidButton from '../../components/LiquidButton';

interface BallType {
    id: number;
    x: number;
    y: number;
    vx: number;
    vy: number;
    isActive: boolean;
}

interface PinType {
    x: number;
    y: number;
}

interface SlotType {
    x: number;
    width: number;
    points: number;
    color: string;
}

const Container = styled.div`
    min-height: 100vh;
    background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%);
    padding: 20px;
    display: flex;
    flex-direction: column;
    align-items: center;

    /* Apply special Pachinko cursor */
    &,
    * {
        cursor: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" fill="%23ff6b6b" stroke="%23ffd700" stroke-width="2"/><circle cx="12" cy="12" r="3" fill="%23ffffff"/></svg>')
                12 12,
            auto !important;
    }

    button {
        cursor: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" fill="%23ffd700" stroke="%23ff6b6b" stroke-width="2"/><polygon points="10,8 14,12 10,16" fill="%23ffffff"/></svg>')
                12 12,
            pointer !important;
    }
`;

const Title = styled.h1`
    color: white;
    text-align: center;
    margin-bottom: 20px;
    font-size: 2.5rem;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
    background: linear-gradient(45deg, #ffd700, #ffed4e);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
`;

const GameContainer = styled.div`
    display: flex;
    gap: 20px;
    max-width: 1200px;
    width: 100%;
`;

const PachinkoMachine = styled.div`
    position: relative;
    width: 400px;
    height: 600px;
    background: linear-gradient(135deg, #c9b037 0%, #e6d66b 50%, #c9b037 100%);
    border: 8px solid #8b7355;
    border-radius: 20px;
    overflow: hidden;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
`;

const PlayField = styled.div`
    position: absolute;
    top: 20px;
    left: 20px;
    right: 20px;
    bottom: 80px;
    background: rgba(0, 0, 0, 0.1);
    border-radius: 10px;
    overflow: hidden;
`;

const PinComponent = styled(motion.div)`
    position: absolute;
    width: 8px;
    height: 8px;
    background: radial-gradient(circle, #c0c0c0 0%, #808080 100%);
    border-radius: 50%;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
`;

const BallComponent = styled(motion.div)`
    position: absolute;
    width: 12px;
    height: 12px;
    background: radial-gradient(circle at 30% 30%, #ff6b6b, #ee5a52);
    border-radius: 50%;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.4);
    z-index: 10;
`;

const SlotComponent = styled(motion.div)<{ points: number }>`
    position: absolute;
    bottom: 0;
    height: 40px;
    background: ${(props) => {
        if (props.points >= 100) return 'linear-gradient(135deg, #ffd700 0%, #ffed4e 100%)';
        if (props.points >= 50) return 'linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%)';
        if (props.points >= 20) return 'linear-gradient(135deg, #4ecdc4 0%, #44a08d 100%)';
        return 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)';
    }};
    border: 2px solid rgba(255, 255, 255, 0.3);
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
    color: white;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
    font-size: 12px;
`;

const ScoreBoard = styled.div`
    background: rgba(0, 0, 0, 0.8);
    color: white;
    padding: 20px;
    border-radius: 15px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
    height: fit-content;
    min-width: 200px;
`;

const ScoreTitle = styled.h2`
    margin: 0 0 15px 0;
    color: #ffd700;
    text-align: center;
    font-size: 1.5rem;
`;

const ScoreValue = styled.div`
    font-size: 2rem;
    font-weight: bold;
    text-align: center;
    margin-bottom: 20px;
    color: #4ecdc4;
`;

const BallCount = styled.div`
    margin-bottom: 20px;
    text-align: center;
`;

const ControlsContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const LaunchButton = styled(LiquidButton)<{ disabled: boolean }>`
    opacity: ${(props) => (props.disabled ? 0.5 : 1)};
    cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
`;

const Pachinko: React.FC = () => {
    const [score, setScore] = useState(0);
    const [ballCount, setBallCount] = useState(10);
    const [balls, setBalls] = useState<BallType[]>([]);
    const [ballIdCounter, setBallIdCounter] = useState(0);

    // Create pins in a triangular pattern
    const pins: PinType[] = useMemo(() => {
        const pinsArray: PinType[] = [];
        for (let row = 0; row < 15; row += 1) {
            const pinsInRow = Math.floor(row / 2) + 3;
            const startX = (360 - (pinsInRow - 1) * 30) / 2;
            for (let col = 0; col < pinsInRow; col += 1) {
                pinsArray.push({
                    x: startX + col * 30,
                    y: 80 + row * 25,
                });
            }
        }
        return pinsArray;
    }, []);

    // Create scoring slots at the bottom
    const slots: SlotType[] = useMemo(
        () => [
            { x: 0, width: 40, points: 10, color: '#a8edea' },
            { x: 40, width: 40, points: 20, color: '#4ecdc4' },
            { x: 80, width: 40, points: 50, color: '#ff6b6b' },
            { x: 120, width: 40, points: 100, color: '#ffd700' },
            { x: 160, width: 40, points: 200, color: '#ffd700' },
            { x: 200, width: 40, points: 100, color: '#ffd700' },
            { x: 240, width: 40, points: 50, color: '#ff6b6b' },
            { x: 280, width: 40, points: 20, color: '#4ecdc4' },
            { x: 320, width: 40, points: 10, color: '#a8edea' },
        ],
        [],
    );

    const launchBall = useCallback(() => {
        if (ballCount <= 0) return;

        setBallCount((prev) => prev - 1);
        setBallIdCounter((prev) => prev + 1);

        const newBall: BallType = {
            id: ballIdCounter,
            x: 180 + (Math.random() - 0.5) * 20, // Launch from center with slight randomness
            y: 10,
            vx: (Math.random() - 0.5) * 2, // Random horizontal velocity
            vy: 0,
            isActive: true,
        };

        setBalls((prev) => [...prev, newBall]);
    }, [ballCount, ballIdCounter]);

    const resetGame = () => {
        setScore(0);
        setBallCount(10);
        setBalls([]);
        setBallIdCounter(0);
    };

    // Physics simulation
    useEffect(() => {
        if (balls.length === 0) return undefined;

        const interval = setInterval(() => {
            setBalls((prevBalls) =>
                prevBalls
                    .map((ball) => {
                        if (!ball.isActive) return ball;

                        let newX = ball.x + ball.vx;
                        let newY = ball.y + ball.vy;
                        let newVx = ball.vx;
                        let newVy = ball.vy + 0.3; // Gravity

                        // Bounce off walls
                        if (newX <= 6 || newX >= 354) {
                            newVx = -newVx * 0.8;
                            newX = Math.max(6, Math.min(354, newX));
                        }

                        // Check collision with pins
                        pins.forEach((pin) => {
                            const dx = newX - pin.x;
                            const dy = newY - pin.y;
                            const distance = Math.sqrt(dx * dx + dy * dy);

                            if (distance < 10) {
                                // Calculate bounce direction
                                const angle = Math.atan2(dy, dx);
                                const bounceForce = 3;
                                newVx = Math.cos(angle) * bounceForce;
                                newVy = Math.sin(angle) * bounceForce;

                                // Move ball away from pin
                                newX = pin.x + Math.cos(angle) * 10;
                                newY = pin.y + Math.sin(angle) * 10;
                            }
                        });

                        // Check if ball reached bottom
                        if (newY >= 520) {
                            // Check which slot the ball landed in
                            const slot = slots.find((s) => newX >= s.x && newX < s.x + s.width);
                            if (slot) {
                                setScore((prev) => prev + slot.points);
                            }
                            return { ...ball, isActive: false };
                        }

                        // Add some friction
                        newVx *= 0.99;
                        newVy *= 0.99;

                        return {
                            ...ball,
                            x: newX,
                            y: newY,
                            vx: newVx,
                            vy: newVy,
                        };
                    })
                    .filter((ball) => ball.isActive || ball.y < 600),
            );
        }, 16); // ~60 FPS

        return () => {
            clearInterval(interval);
        };
    }, [balls.length, pins, slots]);

    // Auto-remove inactive balls after delay
    useEffect(() => {
        const timeout = setTimeout(() => {
            setBalls((prev) => prev.filter((ball) => ball.isActive));
        }, 3000);

        return () => clearTimeout(timeout);
    }, [balls]);

    return (
        <Container>
            <Title>🎯 Pachinko Paradise 🎯</Title>

            <GameContainer>
                <PachinkoMachine>
                    <PlayField>
                        {/* Render Pins */}
                        {pins.map((pin, index) => (
                            <PinComponent
                                key={`pin-${pin.x}-${pin.y}`}
                                style={{ left: pin.x, top: pin.y }}
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                transition={{ delay: index * 0.01 }}
                            />
                        ))}

                        {/* Render Balls */}
                        <AnimatePresence>
                            {balls.map((ball) => (
                                <BallComponent
                                    key={ball.id}
                                    style={{
                                        left: ball.x - 6,
                                        top: ball.y - 6,
                                    }}
                                    initial={{ scale: 0, rotate: 0 }}
                                    animate={{
                                        scale: 1,
                                        rotate: ball.vx * 20,
                                    }}
                                    exit={{ scale: 0, opacity: 0 }}
                                    transition={{ type: 'spring', stiffness: 500 }}
                                />
                            ))}
                        </AnimatePresence>

                        {/* Render Scoring Slots */}
                        {slots.map((slot, index) => (
                            <SlotComponent
                                key={`slot-${slot.x}-${slot.points}`}
                                points={slot.points}
                                style={{
                                    left: slot.x,
                                    width: slot.width,
                                }}
                                whileHover={{ scale: 1.05 }}
                                initial={{ y: 40 }}
                                animate={{ y: 0 }}
                                transition={{ delay: index * 0.1 }}
                            >
                                {slot.points}
                            </SlotComponent>
                        ))}
                    </PlayField>
                </PachinkoMachine>

                <ScoreBoard>
                    <ScoreTitle>Score Board</ScoreTitle>
                    <ScoreValue>{score.toLocaleString()}</ScoreValue>

                    <BallCount>
                        <div>Balls Remaining:</div>
                        <div style={{ fontSize: '1.5rem', color: '#ffd700', fontWeight: 'bold' }}>
                            {ballCount}
                        </div>
                    </BallCount>

                    <ControlsContainer>
                        <LaunchButton onClick={launchBall} disabled={ballCount <= 0}>
                            🚀 Launch Ball
                        </LaunchButton>

                        <LiquidButton onClick={resetGame}>🔄 Reset Game</LiquidButton>
                    </ControlsContainer>

                    {ballCount === 0 && balls.filter((b) => b.isActive).length === 0 && (
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            style={{
                                marginTop: '20px',
                                padding: '15px',
                                background: 'linear-gradient(135deg, #ffd700, #ffed4e)',
                                color: 'black',
                                borderRadius: '10px',
                                textAlign: 'center',
                                fontWeight: 'bold',
                            }}
                        >
                            🎉 Game Over! 🎉
                            <br />
                            Final Score: {score.toLocaleString()}
                        </motion.div>
                    )}
                </ScoreBoard>
            </GameContainer>
        </Container>
    );
};

export default Pachinko;
